import { CommonModule, formatDate } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { XlsxModule, XlsxService } from '@delon/abc/xlsx';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { MomentService } from 'src/app/shared/services/moment.service';

import { ImportCsvService } from '../../services/import-csv.service';

@Component({
  selector: 'app-import-excel',
  templateUrl: './import-excel.component.html',
  standalone: true,
  imports: [
    CommonModule,
    NzButtonModule,
    NzIconModule,
    NzMessageModule,
    XlsxModule
  ],
  providers: [ImportCsvService],
  styles: []
})
export class ImportExcelComponent {
  rawData: any = {};
  data: any[] = [];
  fileName = '';
  selectedFile: File | null = null;

  @Input() extractUrl = '';
  @Input() headerRowNo = 0;
  @Input() columnsMapping: Array<{ key: string; title: string }> = [];
  @Input() customHeaders: string[] = [];

  @Input() processData?: (rows: any[]) => any[];

  @Output() readonly dataChange = new EventEmitter<any[]>();

  constructor(
    private xlsx: XlsxService,
    private importSrv: ImportCsvService,
    private message: NzMessageService,
    private http: HttpClient,
    private msg: NzMessageService,
    private moment: MomentService
  ) {}

  async importFileChanged(e: Event) {
    const node = e.target as HTMLInputElement;
    if (node.files && node.files.length) {
      if (!node.value.includes('.csv')) {
        this.message.error('This file format is invalid');
        this.data = [];
        return;
      }

      // this.fileName = node.files[0].name;
      try {
        this.selectedFile = node.files[0];
        const res = await this.importSrv.parseCsvFile(node.files[0], this.headerRowNo, this.customHeaders);
        if (!this.extractUrl) {
          this.data = [...this.toArray(res)];
          this.rawData = res;

          this.dataChange.emit(this.data);
          this.fileName = this.selectedFile ? this.selectedFile.name : '';
          this.message.success('File uploaded successfully.');
        }
        else {
          let canProceed = true;
          let body = this.toArray(res);
          body.forEach(x => {
            if (!canProceed) return;
            try {
              if (x.docDate) {
                const date = this.moment.toDate(x.docDate);

                if (date.toString() === 'Invalid Date') {
                  throw new Error('Invalid Date');
                }

                x.docDate = formatDate(date, 'yyyy-MM-dd', 'en-US');
              }
              if (x.taxDate) {
                const date = this.moment.toDate(x.taxDate);

                if (date.toString() === 'Invalid Date') {
                  throw new Error('Invalid Date');
                }

                x.taxDate = formatDate(date, 'yyyy-MM-dd', 'en-US');
              }
              if (x.dueDate) {
                const date = this.moment.toDate(x.dueDate);

                if (date.toString() === 'Invalid Date') {
                  throw new Error('Invalid Date');
                }

                x.dueDate = formatDate(date, 'yyyy-MM-dd', 'en-US');
              }
              if (x.postDate) {
                const date = this.moment.toDate(x.postDate);

                if (date.toString() === 'Invalid Date') {
                  throw new Error('Invalid Date');
                }

                x.postDate = formatDate(date, 'yyyy-MM-dd', 'en-US');;
              }
              if (x.startDate) {
                const date = this.moment.toDate(x.startDate);

                if (date.toString() === 'Invalid Date') {
                  throw new Error('Invalid Date');
                }

                x.startDate = formatDate(date, 'yyyy-MM-dd', 'en-US');;
              }
              if (x.projectedEndDate) {
                const date = this.moment.toDate(x.projectedEndDate);

                if (date.toString() === 'Invalid Date') {
                  throw new Error('Invalid Date');
                }

                x.projectedEndDate = formatDate(date, 'yyyy-MM-dd', 'en-US');;
              }
              if (x.endDate) {
                const date = this.moment.toDate(x.endDate);

                if (date.toString() === 'Invalid Date') {
                  throw new Error('Invalid Date');
                }

                x.endDate = formatDate(date, 'yyyy-MM-dd', 'en-US');;
              }
            }
            catch {
              this.msg.error(
                `Invalid Date Value ${x.docDate}. Date format should be YYYYMMDD.`
              );
              this.fileName = '';
              canProceed = false;
            }
          });

          if (!canProceed) return;

          this.http.post(`${this.extractUrl}`, body).subscribe({
            next: (dto: any) => {
              this.data = dto;
              this.rawData = dto;
              this.dataChange.emit(this.data);
              this.fileName = this.selectedFile ? this.selectedFile.name : '';
            },
            error: () => {}
          });
        }
      }
      catch {
        this.message.error(
          'This file contains invalid data and unable to read.'
        );
        this.fileName = '';
      }
      node.value = '';
    }
  }

  public async getDataWithHeaderRowNo(headerRowNo = 0, customHeaders: string[] = []) {
    this.headerRowNo = headerRowNo;
    const res = await this.importSrv.parseCsvFile(this.selectedFile, this.headerRowNo, customHeaders);
    return this.toArray(res);
  }

  private toArray(res: any): any[] {
    let data: any = [];
    if (res) {
      const rows = res as any[];
      if (rows && rows.length > 1) {
        const columns = rows[0];
        //remove required field's asterisk
        for (var i = 0; i < columns.length; i++) {
          const columnTitle = columns[i];
          if (columnTitle.includes('*')) {
            columns[i] = columnTitle.replace('*', '');
          }
          if (columnTitle === 'DepositTo' || columnTitle === 'PaymentBy' || columnTitle === 'DepositTo*' || columnTitle === 'PaymentBy*') {
            columns[i] = 'Account';
          }
          if (columnTitle === 'IsDeferredVat') {
            columns[i] = 'IsDeferredTax';
          }

          if (this.columnsMapping.length) {
            const mappedColumn = this.columnsMapping.find(
              c => c.title === columnTitle
            );
            if (mappedColumn) {
              columns[i] = mappedColumn.key;
            }
          }

          columns[i] = columns[i].charAt(0).toLowerCase() + columns[i].slice(1);
        }

        rows.slice(1).forEach((r: any) => {
          const obj: any = {};
          columns.forEach((c: string, index: number) => {
            if (index < r.length) {
              obj[c] = r[index];
            }
          });
          data.push(obj);
        });
      }
    }

    if (this.processData) {
      data = this.processData(data);
    }
    return data;
  }

  // private toArray(res: any): any[] {
  //   const data: any = [];
  //   if (res) {
  //     const rows = res[Object.keys(res)[0]] as any[];
  //     if (rows && rows.length > 1) {
  //       const columns = rows[0];
  //       //remove required field's asterisk
  //       for(var i=0; i < columns.length; i++) {
  //         if(columns[i].includes('*')) {
  //           columns[i] = columns[i].replace('*', '');
  //         }
  //         if (columns[i] === 'DepositTo' || columns[i] === 'PaymentBy') {
  //           columns[i] = 'Account';
  //         }
  //         if (columns[i] === 'IsDeferredVat') {
  //           columns[i] = 'IsDeferredTax';
  //         }
  //       };

  //       rows.slice(1).forEach((r: any) => {
  //         const obj: any = {};
  //         columns.forEach((c: string, index: number) => {
  //           if (index < r.length) {
  //             obj[c.charAt(0).toLowerCase() + c.slice(1)] = r[index];
  //           }
  //         });
  //         data.push(obj);
  //       });
  //     }
  //   }

  //   return data;
  // }
}
